import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import { BankTransaction } from '@/store/transactionsApi';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useLocalization } from '@/hooks/useLocalization';

export const comparisonOperators = [
  { value: 1, label: 'Is' },
  { value: 2, label: 'Contains' },
  { value: 3, label: 'Begins With' },
  { value: 4, label: 'Ends With' },
];

export interface MakeRuleInlineValue {
  alwaysChange: boolean;
  comparisonOperator: number;
  comparisonText: string;
}

interface MakeRuleFromTransactionProps {
  transaction: BankTransaction;
  currentDescription: string;
  currentCategoryId: number | '';
  savedCategoryId: number | ''; // original category id before edits
  investorId?: number;
  isSaving?: boolean;
  value: MakeRuleInlineValue;
  onChange: (value: MakeRuleInlineValue) => void;
}

export default function MakeRuleFromTransaction(
  props: MakeRuleFromTransactionProps,
) {
  const { t } = useLocalization();
  const {
    transaction,
    currentDescription,
    currentCategoryId,
    savedCategoryId,
    investorId,
    isSaving,
    value,
    onChange,
  } = props;

  const { data: categoriesData } = useGetCashflowCategoriesQuery({
    InvestorId: investorId,
  });

  const idToName: Record<number, string> = useMemo(() => {
    const map: Record<number, string> = {};
    (categoriesData?.categories || []).forEach((c) => {
      map[c.BankTransactionCategoryId] = c.Name;
    });
    return map;
  }, [categoriesData]);

  const savedCategoryName = useMemo(() => {
    if (savedCategoryId !== '' && savedCategoryId !== null) {
      const idNum =
        typeof savedCategoryId === 'string'
          ? parseInt(savedCategoryId)
          : savedCategoryId;
      const name = idToName[idNum];
      if (name) return name;
    }
    return transaction.CategoryName || '';
  }, [savedCategoryId, idToName, transaction.CategoryName]);

  const selectedCategoryName = useMemo(() => {
    if (currentCategoryId !== '' && currentCategoryId !== null) {
      const idNum =
        typeof currentCategoryId === 'string'
          ? parseInt(currentCategoryId)
          : currentCategoryId;
      const name = idToName[idNum];
      if (name) return name;
    }
    return transaction.CategoryName || '';
  }, [currentCategoryId, idToName, transaction.CategoryName]);

  return (
    <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={value.alwaysChange}
            onChange={(e) =>
              onChange({ ...value, alwaysChange: e.target.checked })
            }
            disabled={isSaving}
          />
        }
        label={
          <Typography variant='body2'>
            {t('cashflow.alwaysChangePrefix', {
              defaultValue: 'Always change to',
            })}{' '}
            <b>{currentDescription}</b>{' '}
            {t('cashflow.andCategorizeAs', {
              defaultValue: 'and categorize it as',
            })}{' '}
            <b>{selectedCategoryName}</b>
          </Typography>
        }
      />

      {/* Block layout to mirror legacy: label row, then condition row, then trailing text row */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant='body2'
          sx={{ mb: 1 }}
        >
          {t('cashflow.whenDescription', {
            defaultValue: 'When the description',
          })}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControl
            size='small'
            sx={{ minWidth: 120 }}
            disabled={isSaving || !value.alwaysChange}
          >
            <Select
              value={value.comparisonOperator}
              onChange={(e: SelectChangeEvent<number>) =>
                onChange({
                  ...value,
                  comparisonOperator: e.target.value as number,
                })
              }
            >
              {comparisonOperators.map((op) => (
                <MenuItem
                  key={op.value}
                  value={op.value}
                >
                  {t(`cashflow.operator.${op.value}`, {
                    defaultValue: op.label,
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {value.comparisonOperator === 1 ? (
            <Typography
              variant='body2'
              sx={{ fontWeight: 600 }}
            >
              {transaction.OriginalDescription ||
                transaction.DisplayDescription ||
                ''}
            </Typography>
          ) : (
            <TextField
              size='small'
              value={value.comparisonText}
              onChange={(e) =>
                onChange({ ...value, comparisonText: e.target.value })
              }
              disabled={isSaving || !value.alwaysChange}
              sx={{ width: 320, maxWidth: '100%' }}
            />
          )}
        </Stack>
        <Typography
          variant='body2'
          sx={{ mt: 1 }}
        >
          {t('cashflow.andTheCategoryIs', {
            defaultValue: 'and the category is',
          })}{' '}
          <b>{savedCategoryName}</b>
        </Typography>
      </Box>
    </Box>
  );
}
